import {BaseCommand} from './base.command';
import {CommandType, ContainerChangeSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {MapService} from '../../../api/services/map.service';
import {firstValueFrom, of} from 'rxjs';
import {catchError, defaultIfEmpty} from 'rxjs/operators';
import {UndoLabelMetadata, UndoObjectType} from '../undo-label.utils';

/**
 * ChangeContainerCommand handles parent/container changes for nodes.
 * Supports: drag to parent, extract from container, extract to canvas.
 */
export class ChangeContainerCommand extends BaseCommand<ContainerChangeSnapshot[]> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    private mapService: MapService,
    type: 'change-container' | 'extract-from-container' | 'extract-to-canvas',
    changes: ContainerChangeSnapshot[]
  ) {
    const count = changes.length;
    // Determine object type based on count
    const objectType: UndoObjectType = count === 1 ? 'Node' : 'Selection';
    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: 'Change',
      part: 'Container',
      elementCount: objectType === 'Selection' ? count : undefined
    };

    super(type as CommandType, 'Container Change', {before: changes, after: changes}, labelMetadata);
  }

  async execute(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const changes = this.snapshot.after;
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId) return;

    cy.batch(() => {
      changes.forEach(change => {
        const node = cy.$(`#${change.nodeId}`);
        if (node.length === 0) return;

        // Update position
        node.position(change.afterPosition);

        // Update parent
        if (change.afterParentId) {
          node.move({parent: change.afterParentId});
        } else {
          node.move({parent: null});
        }
      });
    });

    // Update on backend
    const childIds = changes.map(c => c.nodeId);

    if (changes[0]?.afterParentId) {
      // Moving to a parent
      await firstValueFrom(
        this.nodeService.updateParent({
          childIds,
          parentId: changes[0].afterParentId
        })
      );
    } else {
      // Removing from parent
      await firstValueFrom(
        this.nodeService.removeParent({childIds, mapId})
      );
    }

    // Persist positions to backend
    const positionUpdatePromises = changes.map(change =>
        firstValueFrom(
            this.nodeService.updateNodePosition(
                change.nodeId,
                change.afterPosition.x,
                change.afterPosition.y
            ).pipe(
                defaultIfEmpty(null),
                catchError(() => of(null))
            )
        )
    );
    await Promise.all(positionUpdatePromises);
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const changes = this.snapshot.before;
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId) return;

    cy.batch(() => {
      changes.forEach(change => {
        const node = cy.$(`#${change.nodeId}`);
        if (node.length === 0) return;

        // Restore position
        node.position(change.beforePosition);

        // Restore parent
        if (change.beforeParentId) {
          node.move({parent: change.beforeParentId});
        } else {
          node.move({parent: null});
        }
      });
    });

    // Update on backend
    const childIds = changes.map(c => c.nodeId);

    if (changes[0]?.beforeParentId) {
      await firstValueFrom(
        this.nodeService.updateParent({
          childIds,
          parentId: changes[0].beforeParentId
        })
      );
    } else {
      await firstValueFrom(
        this.nodeService.removeParent({childIds, mapId})
      );
    }

    // Persist positions to backend
    const positionUpdatePromises = changes.map(change =>
        firstValueFrom(
            this.nodeService.updateNodePosition(
                change.nodeId,
                change.beforePosition.x,
                change.beforePosition.y
            ).pipe(
                defaultIfEmpty(null),
                catchError(() => of(null))
            )
        )
    );
    await Promise.all(positionUpdatePromises);
  }
}
